import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { FaSearch, FaCalendarAlt, FaUser, FaTag, FaFilter, FaSort, FaComments, FaCalendarCheck, FaHashtag, FaFileAlt, FaChevronDown, FaCheck } from 'react-icons/fa';

const Container = styled.div`
  padding: 12px;
  color: rgba(255, 255, 255, 0.9);
  height: 100%;
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
`;

const Title = styled.h2`
  margin-top: 0;
  margin-bottom: 15px;
  font-size: 18px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 10px;
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  background-color: #2D2D2D;
  border-radius: 4px;
  padding: 8px 12px;
  margin-bottom: 15px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  width: 100%;
  box-sizing: border-box;

  &:focus-within {
    border-color: #4a9eff;
  }
`;

const SearchInput = styled.input`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.9);
  width: 100%;
  margin-left: 8px;
  outline: none;
  font-size: 14px;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const FilterSection = styled.div`
  margin-bottom: 15px;
  width: 100%;
  box-sizing: border-box;
`;

const FilterTitle = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
  color: rgba(255, 255, 255, 0.7);
  
  svg {
    margin-right: 8px;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
  width: 100%;
`;

const FilterButton = styled.button`
  background-color: ${props => props.$active ? '#4a9eff' : '#2D2D2D'};
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid ${props => props.$active ? '#4a9eff' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 4px;
  padding: 6px 10px;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background-color: ${props => props.$active ? '#4a9eff' : 'rgba(255, 255, 255, 0.1)'};
  }

  svg {
    margin-right: 4px;
    min-width: 14px;
  }
`;

const DateRangeContainer = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 12px;
  width: 100%;
`;

const DateInput = styled.input`
  background-color: #2D2D2D;
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  padding: 6px 8px;
  font-size: 12px;
  flex: 1;
  outline: none;
  min-width: 0;
  width: 50%;

  &:focus {
    border-color: #4a9eff;
  }

  &::-webkit-calendar-picker-indicator {
    filter: invert(1);
    opacity: 0.6;
  }
`;

const ResultsContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  margin-top: 10px;
  width: 100%;
`;

const ResultItem = styled.div`
  padding: 12px;
  border-radius: 4px;
  background-color: #2D2D2D;
  margin-bottom: 10px;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
  box-sizing: border-box;

  &:hover {
    background-color: #3D3D3D;
  }
`;

const ResultHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  flex-wrap: wrap;
`;

const ResultTitle = styled.div`
  font-weight: 500;
  font-size: 14px;
  margin-right: 8px;
  flex: 1;
  min-width: 0;
`;

const ResultDate = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  white-space: nowrap;
`;

const ResultPreview = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
`;

const ResultMeta = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  flex-wrap: wrap;
`;

const ResultTag = styled.div`
  display: flex;
  align-items: center;
  white-space: nowrap;
  
  svg {
    margin-right: 4px;
    min-width: 12px;
  }
`;

const NoResults = styled.div`
  text-align: center;
  padding: 30px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
`;

const SortDropdown = styled.select`
  background-color: #2D2D2D;
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  padding: 6px 10px;
  font-size: 12px;
  outline: none;
  cursor: pointer;
  width: 100%;

  &:focus {
    border-color: #4a9eff;
  }
`;

const SearchTypeToggle = styled.div`
  display: flex;
  margin-bottom: 15px;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  width: 100%;
`;

const SearchTypeButton = styled.button`
  background-color: ${props => props.$active ? '#4a9eff' : '#2D2D2D'};
  color: rgba(255, 255, 255, 0.9);
  border: none;
  padding: 8px 12px;
  flex: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  font-size: 13px;

  &:hover {
    background-color: ${props => props.$active ? '#4a9eff' : 'rgba(255, 255, 255, 0.1)'};
  }

  svg {
    margin-right: 6px;
    min-width: 14px;
  }
`;

const KeywordChip = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: #4a9eff;
  color: white;
  border-radius: 16px;
  padding: 4px 10px;
  margin-right: 6px;
  margin-bottom: 6px;
  font-size: 12px;

  span {
    margin-left: 6px;
    cursor: pointer;
    font-weight: bold;
  }
`;

const KeywordsContainer = styled.div`
  margin-bottom: 12px;
  display: flex;
  flex-wrap: wrap;
  width: 100%;
`;

const AdvancedOptionsToggle = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  display: flex;
  align-items: center;
  margin-top: 5px;
  margin-bottom: 10px;
  
  &:hover {
    color: rgba(255, 255, 255, 0.9);
  }
  
  svg {
    margin-right: 5px;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
`;

const Checkbox = styled.input`
  margin-right: 8px;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
`;

// Add these new styled components for the dropdown
const UserDropdownContainer = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 12px;
`;

const UserDropdownButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background-color: #2D2D2D;
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 13px;
  cursor: pointer;
  text-align: left;

  &:hover {
    background-color: #3D3D3D;
  }

  svg {
    margin-left: 8px;
  }
`;

const UserDropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  max-height: 200px;
  overflow-y: auto;
  background-color: #2D2D2D;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  margin-top: 4px;
  z-index: 10;
  display: ${props => props.$isOpen ? 'block' : 'none'};
`;

const UserDropdownItem = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.9);

  &:hover {
    background-color: #3D3D3D;
  }

  svg {
    margin-right: 8px;
    color: ${props => props.$selected ? '#4a9eff' : 'transparent'};
  }
`;

const SelectedUsersDisplay = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`;

const SelectedUserChip = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: #3D3D3D;
  color: rgba(255, 255, 255, 0.9);
  border-radius: 16px;
  padding: 4px 10px;
  font-size: 12px;

  span {
    margin-left: 6px;
    cursor: pointer;
    font-weight: bold;
  }
`;

const DBSearch = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchType, setSearchType] = useState('conversations'); // 'conversations' or 'calendar'
  const [activeFilters, setActiveFilters] = useState({
    timeframe: 'all',
    contentType: [],
    users: []
  });
  const [sortBy, setSortBy] = useState('recent');
  const [keywords, setKeywords] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [exactMatch, setExactMatch] = useState(false);
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [searchInAttachments, setSearchInAttachments] = useState(true);
  
  // New state for user dropdown
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const userDropdownRef = useRef(null);
  
  // Available users list
  const availableUsers = [
    { id: 'john', name: 'John Doe' },
    { id: 'jane', name: 'Jane Smith' },
    { id: 'mike', name: 'Mike Johnson' },
    { id: 'sarah', name: 'Sarah Williams' },
    { id: 'alex', name: 'Alex Brown' },
    { id: 'lisa', name: 'Lisa Garcia' }
  ];
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setIsUserDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Mock data for demonstration
  const mockConversations = [
    {
      id: 1,
      title: "Project Planning Discussion",
      preview: "We discussed the timeline for the new feature rollout and assigned tasks to team members...",
      date: "2023-10-15",
      user: "John Doe",
      tags: ["Meeting", "Planning"],
      type: "conversation"
    },
    {
      id: 2,
      title: "Customer Support Conversation",
      preview: "The customer reported issues with login functionality. We provided troubleshooting steps...",
      date: "2023-10-12",
      user: "Jane Smith",
      tags: ["Support", "Technical"],
      type: "conversation"
    },
    {
      id: 3,
      title: "Marketing Strategy Review",
      preview: "Analyzed the performance of our recent campaign and discussed adjustments for Q4...",
      date: "2023-10-08",
      user: "Mike Johnson",
      tags: ["Marketing", "Strategy"],
      type: "conversation"
    }
  ];
  
  const mockCalendarEvents = [
    {
      id: 101,
      title: "Weekly Team Standup",
      preview: "Regular team meeting to discuss progress and blockers",
      date: "2023-10-16",
      time: "10:00 AM - 10:30 AM",
      attendees: ["John Doe", "Jane Smith", "Mike Johnson"],
      type: "calendar"
    },
    {
      id: 102,
      title: "Client Presentation",
      preview: "Presenting the new dashboard features to the client",
      date: "2023-10-18",
      time: "2:00 PM - 3:30 PM",
      attendees: ["John Doe", "Sarah Williams"],
      type: "calendar"
    },
    {
      id: 103,
      title: "Product Planning",
      preview: "Quarterly product roadmap planning session",
      date: "2023-10-20",
      time: "9:00 AM - 12:00 PM",
      attendees: ["Mike Johnson", "Jane Smith", "Alex Brown"],
      type: "calendar"
    }
  ];

  // Get results based on search type
  const getResults = () => {
    return searchType === 'conversations' ? mockConversations : mockCalendarEvents;
  };

  const toggleTimeframeFilter = (filter) => {
    setActiveFilters({
      ...activeFilters,
      timeframe: activeFilters.timeframe === filter ? 'all' : filter
    });
  };

  const toggleContentTypeFilter = (filter) => {
    setActiveFilters({
      ...activeFilters,
      contentType: activeFilters.contentType.includes(filter)
        ? activeFilters.contentType.filter(item => item !== filter)
        : [...activeFilters.contentType, filter]
    });
  };

  const toggleUserSelection = (userId) => {
    setActiveFilters({
      ...activeFilters,
      users: activeFilters.users.includes(userId)
        ? activeFilters.users.filter(id => id !== userId)
        : [...activeFilters.users, userId]
    });
  };
  
  const selectAllUsers = () => {
    if (activeFilters.users.length === availableUsers.length) {
      // If all are selected, deselect all
      setActiveFilters({
        ...activeFilters,
        users: []
      });
    } else {
      // Otherwise select all
      setActiveFilters({
        ...activeFilters,
        users: availableUsers.map(user => user.id)
      });
    }
  };
  
  const removeUserSelection = (userId) => {
    setActiveFilters({
      ...activeFilters,
      users: activeFilters.users.filter(id => id !== userId)
    });
  };
  
  const getUserDisplayText = () => {
    if (activeFilters.users.length === 0) {
      return 'All Users';
    } else if (activeFilters.users.length === availableUsers.length) {
      return 'All Users Selected';
    } else if (activeFilters.users.length === 1) {
      const selectedUser = availableUsers.find(user => user.id === activeFilters.users[0]);
      return selectedUser ? selectedUser.name : 'One User Selected';
    } else {
      return `${activeFilters.users.length} Users Selected`;
    }
  };
  
  const handleSearchInput = (e) => {
    setSearchQuery(e.target.value);
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      addKeyword(searchQuery.trim());
      setSearchQuery('');
    }
  };
  
  const addKeyword = (keyword) => {
    if (!keywords.includes(keyword)) {
      setKeywords([...keywords, keyword]);
    }
  };
  
  const removeKeyword = (keyword) => {
    setKeywords(keywords.filter(k => k !== keyword));
  };

  return (
    <Container>
      <Title>Database Search</Title>
      
      <SearchTypeToggle>
        <SearchTypeButton 
          $active={searchType === 'conversations'} 
          onClick={() => setSearchType('conversations')}
        >
          <FaComments />
          Conversations
        </SearchTypeButton>
        <SearchTypeButton 
          $active={searchType === 'calendar'} 
          onClick={() => setSearchType('calendar')}
        >
          <FaCalendarCheck />
          Calendar
        </SearchTypeButton>
      </SearchTypeToggle>
      
      <SearchBar>
        <FaSearch color="rgba(255, 255, 255, 0.6)" />
        <SearchInput 
          placeholder={`Search ${searchType}...`} 
          value={searchQuery}
          onChange={handleSearchInput}
          onKeyDown={handleKeyDown}
        />
      </SearchBar>
      
      {keywords.length > 0 && (
        <KeywordsContainer>
          {keywords.map((keyword, index) => (
            <KeywordChip key={index}>
              {keyword}
              <span onClick={() => removeKeyword(keyword)}>×</span>
            </KeywordChip>
          ))}
        </KeywordsContainer>
      )}
      
      <AdvancedOptionsToggle onClick={() => setShowAdvanced(!showAdvanced)}>
        <FaFilter />
        {showAdvanced ? 'Hide advanced options' : 'Show advanced options'}
      </AdvancedOptionsToggle>
      
      {showAdvanced && (
        <>
          <CheckboxContainer>
            <Checkbox 
              type="checkbox" 
              id="exactMatch" 
              checked={exactMatch} 
              onChange={() => setExactMatch(!exactMatch)} 
            />
            <CheckboxLabel htmlFor="exactMatch">Exact phrase match</CheckboxLabel>
          </CheckboxContainer>
          
          <CheckboxContainer>
            <Checkbox 
              type="checkbox" 
              id="caseSensitive" 
              checked={caseSensitive} 
              onChange={() => setCaseSensitive(!caseSensitive)} 
            />
            <CheckboxLabel htmlFor="caseSensitive">Case sensitive</CheckboxLabel>
          </CheckboxContainer>
          
          <CheckboxContainer>
            <Checkbox 
              type="checkbox" 
              id="searchAttachments" 
              checked={searchInAttachments} 
              onChange={() => setSearchInAttachments(!searchInAttachments)} 
            />
            <CheckboxLabel htmlFor="searchAttachments">Search in attachments</CheckboxLabel>
          </CheckboxContainer>
        </>
      )}
      
      <FilterSection>
        <FilterTitle>
          <FaCalendarAlt />
          Time Range
        </FilterTitle>
        <FilterGroup>
          <FilterButton 
            $active={activeFilters.timeframe === 'today'} 
            onClick={() => toggleTimeframeFilter('today')}
          >
            Today
          </FilterButton>
          <FilterButton 
            $active={activeFilters.timeframe === 'week'} 
            onClick={() => toggleTimeframeFilter('week')}
          >
            This Week
          </FilterButton>
          <FilterButton 
            $active={activeFilters.timeframe === 'month'} 
            onClick={() => toggleTimeframeFilter('month')}
          >
            This Month
          </FilterButton>
          <FilterButton 
            $active={activeFilters.timeframe === 'year'} 
            onClick={() => toggleTimeframeFilter('year')}
          >
            This Year
          </FilterButton>
        </FilterGroup>
        
        <DateRangeContainer>
          <DateInput 
            type="date" 
            placeholder="From" 
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
          <DateInput 
            type="date" 
            placeholder="To" 
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </DateRangeContainer>
      </FilterSection>
      
      <FilterSection>
        <FilterTitle>
          <FaUser />
          {searchType === 'conversations' ? 'Users' : 'Attendees'}
        </FilterTitle>
        
        <UserDropdownContainer ref={userDropdownRef}>
          <UserDropdownButton onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}>
            {getUserDisplayText()}
            <FaChevronDown size={12} />
          </UserDropdownButton>
          
          <UserDropdownMenu $isOpen={isUserDropdownOpen}>
            <UserDropdownItem 
              onClick={selectAllUsers}
              $selected={activeFilters.users.length === availableUsers.length}
            >
              <FaCheck />
              Select All
            </UserDropdownItem>
            
            {availableUsers.map(user => (
              <UserDropdownItem 
                key={user.id}
                onClick={() => toggleUserSelection(user.id)}
                $selected={activeFilters.users.includes(user.id)}
              >
                <FaCheck />
                {user.name}
              </UserDropdownItem>
            ))}
          </UserDropdownMenu>
        </UserDropdownContainer>
        
        {activeFilters.users.length > 0 && (
          <SelectedUsersDisplay>
            {activeFilters.users.map(userId => {
              const user = availableUsers.find(u => u.id === userId);
              return user ? (
                <SelectedUserChip key={userId}>
                  {user.name}
                  <span onClick={() => removeUserSelection(userId)}>×</span>
                </SelectedUserChip>
              ) : null;
            })}
          </SelectedUsersDisplay>
        )}
      </FilterSection>
      
      {searchType === 'conversations' && (
        <FilterSection>
          <FilterTitle>
            <FaTag />
            Content Type
          </FilterTitle>
          <FilterGroup>
            <FilterButton 
              $active={activeFilters.contentType.includes('chat')} 
              onClick={() => toggleContentTypeFilter('chat')}
            >
              <FaComments />
              Chat
            </FilterButton>
            <FilterButton 
              $active={activeFilters.contentType.includes('meeting')} 
              onClick={() => toggleContentTypeFilter('meeting')}
            >
              <FaCalendarCheck />
              Meeting
            </FilterButton>
            <FilterButton 
              $active={activeFilters.contentType.includes('support')} 
              onClick={() => toggleContentTypeFilter('support')}
            >
              <FaUser />
              Support
            </FilterButton>
            <FilterButton 
              $active={activeFilters.contentType.includes('notes')} 
              onClick={() => toggleContentTypeFilter('notes')}
            >
              <FaFileAlt />
              Notes
            </FilterButton>
          </FilterGroup>
        </FilterSection>
      )}
      
      {searchType === 'calendar' && (
        <FilterSection>
          <FilterTitle>
            <FaHashtag />
            Event Type
          </FilterTitle>
          <FilterGroup>
            <FilterButton 
              $active={activeFilters.contentType.includes('meeting')} 
              onClick={() => toggleContentTypeFilter('meeting')}
            >
              Meeting
            </FilterButton>
            <FilterButton 
              $active={activeFilters.contentType.includes('presentation')} 
              onClick={() => toggleContentTypeFilter('presentation')}
            >
              Presentation
            </FilterButton>
            <FilterButton 
              $active={activeFilters.contentType.includes('planning')} 
              onClick={() => toggleContentTypeFilter('planning')}
            >
              Planning
            </FilterButton>
            <FilterButton 
              $active={activeFilters.contentType.includes('1on1')} 
              onClick={() => toggleContentTypeFilter('1on1')}
            >
              1-on-1
            </FilterButton>
          </FilterGroup>
        </FilterSection>
      )}
      
      <FilterSection>
        <FilterTitle>
          <FaSort />
          Sort By
        </FilterTitle>
        <SortDropdown value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          <option value="recent">Most Recent</option>
          <option value="oldest">Oldest First</option>
          <option value="relevance">Relevance</option>
          {searchType === 'conversations' ? (
            <option value="user">User</option>
          ) : (
            <option value="attendees">Attendees</option>
          )}
        </SortDropdown>
      </FilterSection>
      
      <ResultsContainer>
        {getResults().length > 0 ? (
          getResults().map(result => (
            <ResultItem key={result.id}>
              <ResultHeader>
                <ResultTitle>{result.title}</ResultTitle>
                <ResultDate>
                  {result.date}
                  {result.type === 'calendar' && ` • ${result.time}`}
                </ResultDate>
              </ResultHeader>
              <ResultPreview>{result.preview}</ResultPreview>
              <ResultMeta>
                {result.type === 'conversation' ? (
                  <>
                    <ResultTag>
                      <FaUser />
                      {result.user}
                    </ResultTag>
                    {result.tags.map((tag, index) => (
                      <ResultTag key={index}>
                        <FaTag />
                        {tag}
                      </ResultTag>
                    ))}
                  </>
                ) : (
                  <>
                    <ResultTag>
                      <FaUser />
                      {result.attendees.length} attendees
                    </ResultTag>
                  </>
                )}
              </ResultMeta>
            </ResultItem>
          ))
        ) : (
          <NoResults>No results found. Try adjusting your search criteria.</NoResults>
        )}
      </ResultsContainer>
    </Container>
  );
};

export default DBSearch; 